import { useEffect, useMemo, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Badge, type BadgeVariant } from '@heyroot/ui/primitives/Badge';
import { EmptyState } from '@heyroot/ui/primitives/EmptyState';
import { Input } from '@heyroot/ui/primitives/Input';
import { Pagination } from '@heyroot/ui/primitives/Pagination';
import { SkeletonTable } from '@heyroot/ui/primitives/Skeleton';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableHeaderCell,
} from '@heyroot/ui/primitives/Table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@heyroot/backoffice-ui';
import { fetchPeople, queryKeys, type PersonRow } from '../lib/api';

const PAGE_SIZE = 25;
const DEBOUNCE_MS = 300;

// Status filter values are the tenant_users.status CHECK values. There is no
// locked-only filter: platform.users has no lock column, and the locked_until
// columns that exist are tenant-scoped PIN lockout -- the wrong grain. Named as
// a V1 divergence in the slice spec (§9).
const STATUSES = ['active', 'suspended', 'removed'] as const;

function membershipVariant(hasActive: boolean): BadgeVariant {
  return hasActive ? 'success' : 'neutral';
}

export function UsersListPage() {
  const [rawSearch, setRawSearch] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string>('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(rawSearch);
      setPage(1);
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [rawSearch]);

  const params = useMemo(
    () => ({
      ...(search ? { q: search } : {}),
      ...(status ? { status } : {}),
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    }),
    [search, status, page],
  );

  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.people(params),
    queryFn: () => fetchPeople(params),
    placeholderData: keepPreviousData,
  });

  return (
    <section className="flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h1 className="text-heading-2 text-primary">People</h1>
        <p className="text-body-sm text-secondary">
          Everyone with an account, across every tenant. Search by email.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={rawSearch}
          onChange={(e) => setRawSearch(e.target.value)}
          placeholder="Search by email"
          aria-label="Search people by email"
          className="max-w-xs"
        />
        <Select
          value={status}
          onValueChange={(v) => {
            setStatus(v === 'all' ? '' : v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-40" aria-label="Filter by membership status">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        // SkeletonTable's prop is `cols`, not `columns`, and it renders bare <tr>
        // elements -- so it must sit inside a real table, not replace one.
        <div className="overflow-x-auto">
          <Table>
            <TableBody>
              <SkeletonTable rows={8} cols={5} />
            </TableBody>
          </Table>
        </div>
      ) : isError ? (
        <p className="text-body-sm text-danger-text">Could not load people. Retry in a moment.</p>
      ) : !data || data.rows.length === 0 ? (
        <EmptyState
          title="No people match"
          description="No people match this search. Try a different email fragment or clear the status filter."
        />
      ) : (
        <>
          <div className="overflow-x-auto">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Email</TableHeaderCell>
                  <TableHeaderCell>Tenants</TableHeaderCell>
                  <TableHeaderCell>Roles</TableHeaderCell>
                  <TableHeaderCell>Membership</TableHeaderCell>
                  <TableHeaderCell>Created</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.rows.map((p: PersonRow) => (
                  <TableRow key={p.id}>
                    <TableCell>
                      <Link to="/users/$userId" params={{ userId: p.id }}>
                        {p.email}
                      </Link>
                    </TableCell>
                    <TableCell className="max-w-xs">
                      {p.tenantNames.length ? p.tenantNames.join(', ') : '—'}
                    </TableCell>
                    <TableCell>{p.roles.length ? p.roles.join(', ') : '—'}</TableCell>
                    <TableCell>
                      <Badge variant={membershipVariant(p.hasActiveMembership)}>
                        {p.hasActiveMembership ? 'Active' : 'None'}
                      </Badge>
                    </TableCell>
                    <TableCell>{new Date(p.createdAt).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Pagination page={page} total={data.total} pageSize={PAGE_SIZE} onPageChange={setPage} />
        </>
      )}
    </section>
  );
}
